import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { hoursLabel, money, pct, rate } from "./data";

/**
 * The Performance column's hover detail — anchored to the row it's about, a
 * small authored moment instead of a plain tooltip. Portaled to <body> (the
 * table's own .lbr-scroll clips anything absolute inside it — see labor.css)
 * and positioned from the hovered cell's own rect, same technique as
 * ComponentCard's "…" menu (dashboards.css .db-pop).
 *
 * One timeline, not four separate ones: a single 0→1 eased progress value
 * drives the ring, the count-up number AND the terms' stagger together, so
 * nothing can drift out of sync with anything else. Two things replay every
 * time the row is re-hovered — that repeatability is what makes it read as
 * an authored sequence rather than a one-off animation.
 */

const RING_R = 42;
const RING_C = 2 * Math.PI * RING_R;

const reduceMotion = () =>
  typeof window !== "undefined"
  && window.matchMedia
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** One rAF loop, eased 0→1, restarting every time `runId` changes. */
const useProgress = (runId, duration = 900) => {
  const [p, setP] = useState(0);
  useEffect(() => {
    if (runId == null) { setP(0); return undefined; }
    if (reduceMotion()) { setP(1); return undefined; }
    let raf;
    const start = performance.now();
    setP(0);
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3; // ease-out cubic — the same confident-arrival curve as the CSS transitions in labor.css
      setP(eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [runId, duration]);
  return p;
};

/** Same headroom rule the row's own mini rail uses (index.jsx) — ring and
    rail always read as the same shape at two sizes, never two disagreeing
    ones for the same number. */
const scaleFor = (perfPct, goalPct) => Math.max(perfPct || 0, goalPct || 0, 1) * 1.25;

const Term = ({ show, className, children }) => (
  <span className={`lbr-pop-term${className ? ` ${className}` : ""}${show ? " in" : ""}`}>{children}</span>
);
Term.propTypes = { show: PropTypes.bool.isRequired, className: PropTypes.string, children: PropTypes.node.isRequired };
Term.defaultProps = { className: "" };

const Stat = ({ show, label, value, delay }) => (
  <div className={`lbr-pop-stat${show ? " in" : ""}`} style={{ transitionDelay: show ? `${delay}ms` : "0ms" }}>
    <span>{label}</span>
    <b>{value}</b>
  </div>
);
Stat.propTypes = {
  show: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  delay: PropTypes.number.isRequired,
};

const PerfPopover = ({ row, name, tone, revenue, anchor, active }) => {
  const p = useProgress(active ? row.key : null, 900);
  const settled = p >= 1;

  /* Mounted with the entrance already "off" — opacity:0, and .lbr-pop's own
     clip-path starts at its closed inset (labor.css) — flipped on next frame
     so adding `.in` is an actual state change for the clip-path transition
     and the .lbr-pop-inner keyframes to animate FROM. Without this the card
     would just appear already open, since React mounts it with `active` true
     from the first render. The parent keeps this mounted a beat after
     `active` goes false (see index.jsx) so there's time for a clean fade-out
     instead of the popover vanishing mid-frame. */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  const shown = mounted && active;

  const scale = scaleFor(row.perfPct, row.goalPct);
  const perfFrac = Math.max(0, Math.min(1, (row.perfPct || 0) / scale));
  const goalFrac = row.goalPct > 0 ? Math.max(0, Math.min(1, row.goalPct / scale)) : null;
  const shownPct = (row.perfPct || 0) * p;
  const dashoffset = RING_C * (1 - perfFrac * p);

  const note = row.over ? "over its share of revenue" : "under its share of revenue";
  const hasGoal = row.deltaAmt != null;
  let statusLabel = "No goal set";
  if (hasGoal) statusLabel = row.over ? "Over" : "Under";
  let deltaLabel = "Goal";
  if (hasGoal) deltaLabel = row.over ? "Over by" : "Under by";
  let deltaValue = "—";
  if (hasGoal) deltaValue = money(Math.abs(row.deltaAmt));
  else if (row.goalPct > 0) deltaValue = money(row.goalAmt);

  /* Placement: prefer opening above-right of the cell (the table scrolls
     downward, so there's usually more room above a row than below the last
     one); flip below when the row is near the top of the card, and clamp
     horizontally so a Goal-$-column hover near the right edge never runs the
     popover off the viewport. */
  const style = useMemo(() => {
    if (!anchor) return null;
    const W = 300;
    const H = 372;
    const GAP = 10;
    const openAbove = anchor.top > H + GAP + 12;
    const top = openAbove ? anchor.top - H - GAP : Math.min(anchor.bottom + GAP, window.innerHeight - H - 12);
    let left = anchor.left + anchor.width / 2 - W / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - W - 12));
    const caretLeft = Math.max(16, Math.min(W - 16, anchor.left + anchor.width / 2 - left));
    return { top, left, width: W, caretLeft, openAbove };
  }, [anchor]);

  if (!style) return null;

  return createPortal(
    <div
      className={`lbr-pop${shown ? " in" : ""}${style.openAbove ? " above" : " below"}`}
      style={{ top: style.top, left: style.left, width: style.width, "--caret-x": `${style.caretLeft}px` }}
      role="tooltip"
    >
      {/* Two independent, always-valid motions instead of one hand-tuned
          polygon: this wrapper tapers horizontally out of the caret point
          (transform, cheap and can't glitch); .lbr-pop itself reveals
          vertically via clip-path: inset() (also cheap, and — unlike
          polygon() — topologically incapable of the points-crossing-mid-
          transition flicker that plain shape animations are prone to). */}
      <div className="lbr-pop-inner">
        <div className="lbr-pop-h">
          <i className="lbr-pop-dot" style={{ background: tone.ink }} />
          <span className="lbr-pop-name">{name}</span>
          <span className="lbr-pop-pill" style={{ color: tone.ink, background: tone.soft }}>
            {statusLabel}
          </span>
        </div>

        <div className="lbr-pop-ring-wrap">
          <svg className="lbr-pop-ring" viewBox="0 0 100 100" aria-hidden="true">
            <circle className="track" cx="50" cy="50" r={RING_R} />
            {goalFrac != null && (
              <line
                className="goal"
                x1="50" y1="4" x2="50" y2="14"
                transform={`rotate(${-90 + goalFrac * 360} 50 50)`}
              />
            )}
            <circle
              className="fill"
              cx="50" cy="50" r={RING_R}
              style={{
                stroke: tone.ink,
                strokeDasharray: RING_C,
                strokeDashoffset: dashoffset,
              }}
            />
          </svg>
          <div className="lbr-pop-ring-mid">
            <b style={{ color: tone.ink }}>{pct(shownPct)}</b>
            <span>of revenue</span>
          </div>
        </div>

        <div className="lbr-pop-eq">
          <Term show={p > 0.15}>{money(row.cost)}</Term>
          <Term show={p > 0.3} className="op">÷</Term>
          <Term show={p > 0.42}>{money(revenue)}</Term>
          <Term show={p > 0.58} className="op">=</Term>
          <Term show={p > 0.7} className="result">{pct(row.perfPct)}</Term>
        </div>

        <p className={`lbr-pop-note${settled ? " in" : ""}`}>
          <b>{name}</b> is running <b style={{ color: tone.ink }}>{note}</b>
          {row.goalPct > 0 && <> — goal is {pct(row.goalPct)} of revenue</>}.
        </p>

        <div className="lbr-pop-stats">
          <Stat show={settled} label="Hours" value={hoursLabel(row.hours)} delay={0} />
          <Stat show={settled} label="Pay/hour" value={row.payPerHour == null ? "—" : rate(row.payPerHour)} delay={40} />
          <Stat show={settled} label="Total cost" value={money(row.cost)} delay={80} />
          <Stat show={settled} label={deltaLabel} value={deltaValue} delay={120} />
        </div>

        {!row.live && <p className="lbr-pop-typed">Hours are typed in — no live metric feeds this department.</p>}
      </div>

      <i className="lbr-pop-caret" />
    </div>,
    document.body,
  );
};

PerfPopover.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  row: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  tone: PropTypes.shape({ ink: PropTypes.string, soft: PropTypes.string }).isRequired,
  revenue: PropTypes.number.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  anchor: PropTypes.object,
  active: PropTypes.bool.isRequired,
};
PerfPopover.defaultProps = { anchor: null };

export default PerfPopover;
